'use client';

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { NewsCard } from '@/components/news';
import { api, News } from '@/lib/api';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { Plus } from 'lucide-react';
import { useAuth } from '@/providers/AuthProvider';
import { useTranslations } from 'next-intl';
import { SearchSortBar, SortBy, SortDirection } from '@/components/ui';

export default function NewsPage() {
    const t = useTranslations('landing.newsList');
    const [newsItems, setNewsItems] = useState<News[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const { user } = useAuth();

    // Search and sort state
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState<SortBy>('updated');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    const handleSearchChange = useCallback((value: string) => {
        setSearchQuery(value);
    }, []);

    const handleSortChange = useCallback((by: SortBy, direction: SortDirection) => {
        setSortBy(by);
        setSortDirection(direction);
    }, []);

    // Filter and sort news
    const filteredNews = useMemo(() => {
        let result = [...newsItems];

        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            result = result.filter(item =>
                (item.title?.toLowerCase().includes(query)) ||
                (item.description?.toLowerCase().includes(query)) ||
                (item.content?.toLowerCase().includes(query))
            );
        }

        // Apply sorting
        result.sort((a, b) => {
            if (sortBy === 'alphabetical') {
                return sortDirection === 'asc'
                    ? a.title.localeCompare(b.title)
                    : b.title.localeCompare(a.title);
            } else {
                const dateA = new Date(a.updated_at).getTime();
                const dateB = new Date(b.updated_at).getTime();
                return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
            }
        });

        return result;
    }, [newsItems, searchQuery, sortBy, sortDirection]);

    useEffect(() => {
        const fetchNews = async () => {
            try {
                const data = await api.getNews(50); // Fetch more items for the main page
                setNewsItems(data);
            } catch (error) {
                console.error("Failed to fetch news:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchNews();
    }, []);

    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    return (
        <main className="min-h-screen pt-24 pb-20 px-6 md:px-10 bg-[#F9F9F9] flex flex-col">
            <div className="w-full max-w-8xl mx-auto flex flex-col flex-1">
                <div className="flex items-center justify-between mb-10">
                    <div>
                        <h1 className="text-3xl font-medium tracking-tight text-black mb-1">{t('title')}</h1>
                        <p className="text-[#888888]">
                            {filteredNews.length === 1
                                ? t('newsCount', { count: 1 })
                                : t('newsCountPlural', { count: filteredNews.length })}
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <SearchSortBar
                            searchValue={searchQuery}
                            onSearchChange={handleSearchChange}
                            sortBy={sortBy}
                            sortDirection={sortDirection}
                            onSortChange={handleSortChange}
                        />

                        {user?.role === 'root' && (
                            <Link href="/news/create">
                                <button
                                    className="flex items-center justify-center p-2 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
                                    title={t('create')}
                                >
                                    <Plus className="w-5 h-5" />
                                </button>
                            </Link>
                        )}
                    </div>
                </div>

                {isLoading ? (
                    <div className="flex-1 flex items-center justify-center">
                        <div className="w-8 h-8 border-2 border-slate-200 border-t-black rounded-full animate-spin"></div>
                    </div>
                ) : (
                    <>
                        {filteredNews.length > 0 ? (
                            <motion.div
                                key={`${searchQuery}-${sortBy}-${sortDirection}`}
                                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                                initial="initial"
                                animate="animate"
                                variants={{
                                    initial: { opacity: 0 },
                                    animate: {
                                        opacity: 1,
                                        transition: { staggerChildren: 0.1 }
                                    }
                                }}
                            >
                                {filteredNews.map((item) => (
                                    <motion.div
                                        key={item.id}
                                        className="h-[280px]"
                                        variants={{
                                            initial: { opacity: 0, y: 20 },
                                            animate: { opacity: 1, y: 0 }
                                        }}
                                    >
                                        <NewsCard
                                            title={item.title}
                                            description={item.description}
                                            date={item.published_at}
                                            imageUrl={item.image_url || '/placeholder-news.jpg'}
                                            slug={item.slug}
                                            className="h-[280px]"
                                            deleteStatus={
                                                deletingId === item.id
                                                    ? 'deleting'
                                                    : confirmDeleteId === item.id
                                                        ? 'confirming'
                                                        : 'idle'
                                            }
                                            onDelete={user?.role === 'root' ? async (e) => {
                                                if (confirmDeleteId === item.id) {
                                                    // Second click - proceed with delete
                                                    setDeletingId(item.id);
                                                    try {
                                                        await api.deleteNews(item.id);
                                                        setNewsItems(prev => prev.filter(n => n.id !== item.id));
                                                        setConfirmDeleteId(null);
                                                    } catch (error) {
                                                        console.error("Failed to delete news:", error);
                                                        alert("Failed to delete news article");
                                                    } finally {
                                                        setDeletingId(null);
                                                    }
                                                } else {
                                                    // First click - ask for confirmation
                                                    setConfirmDeleteId(item.id);
                                                    setTimeout(() => setConfirmDeleteId(null), 3000);
                                                }
                                            } : undefined}
                                        />
                                    </motion.div>
                                ))}
                            </motion.div>
                        ) : (
                            <div className="text-center py-20 text-slate-500">
                                {t('noNews')}
                            </div>
                        )}
                    </>
                )}
            </div>
        </main>
    );
}
